/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import {
    type Location,
    type Params,
    type SetURLSearchParams,
    useLocation,
    useParams,
    useSearchParams
} from "react-router-dom"

import {
    type TCoachHeaderGroupMetaState as TGroupState,
    type TCoachHeaderParticipantMetaState as TParticipantState
} from "@/3514/components"
import { CoachHeaderTabs } from "@/3514/components/coach/header-tabs/coach.header-tabs.component"
import { type TCoachHeaderTab } from "@/3514/components/coach/header-tabs/coach.header-tabs.types"
import { Avatar } from "@/3514/components/shared/avatar/avatar.component"
import { EAvatarSize } from "@/3514/components/shared/avatar/avatar.types"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { ERoute } from "@/3514/constants"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { buildCssClassName, getCurrentRouteFromPathname } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import styles from "./header-meta.styles"
import { HeaderMetaView } from "./header-meta.view"
import config from "./header.meta.config"

type _TProps = {
    isVisible: boolean
    isReady: boolean
    participantState: TParticipantState
    groupState: TGroupState
}
const HeaderMetaCoach: FC<_TProps> = ({ isVisible, isReady, participantState, groupState }: _TProps): ReactElement => {
    const qc: QueryClient = useQueryClient()

    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { pathname }: Location = useLocation()

    const { groupId: groupIdParam, participantId: participantIdParam }: Readonly<Params> = useParams()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const tab: string = searchParams.get("tab")
    const sessionIdParam: string = searchParams.get("sessionId")
    const moduleIdParam: string | null = searchParams.get("moduleId")

    const lastModuleIdParamRef: RefObject<number> = useRef<number>(null)
    const lastSessionIdParamRef: RefObject<number> = useRef<number>(null)

    useEffect((): void => {
        if (moduleIdParam === null) return

        lastModuleIdParamRef.current = Number(moduleIdParam)
    }, [moduleIdParam])

    useEffect((): void => {
        if (sessionIdParam === null) return

        lastSessionIdParamRef.current = Number(sessionIdParam)
    }, [sessionIdParam])

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const currentRoute: ERoute = useMemo<ERoute>((): ERoute => getCurrentRouteFromPathname(pathname), [pathname])

    const isGroup: boolean = currentRoute === ERoute.Coach_Group
    const isGroupParticipant: boolean = currentRoute === ERoute.Coach_GroupParticipant
    const isIndividualParticipant: boolean = currentRoute === ERoute.Coach_IndividualParticipant

    const tabs: TCoachHeaderTab[] = useMemo<TCoachHeaderTab[]>(
        (): TCoachHeaderTab[] =>
            config.renderCoachTabs({
                groupId: Number(groupIdParam),
                participantId: Number(participantIdParam),
                lastModuleId: lastModuleIdParamRef.current,
                lastSessionId: lastSessionIdParamRef.current,
                tab,
                qc,
                t
            })[currentRoute],
        [groupIdParam, participantIdParam, tab]
    )

    function renderIndividualMeta(): ReactElement {
        return (
            <div className={styles[EUserRole.Coach].metaWrapper()}>
                <Avatar
                    url={participantState.photo}
                    userData={{
                        firstName: participantState.firstName,
                        lastName: participantState.lastName
                    }}
                    className={styles[EUserRole.Coach].loadingValue({
                        isBlurred: !isReady,
                        intensity: 3
                    })}
                    size={isMobile ? EAvatarSize._30 : EAvatarSize._40}
                    isLettered
                />

                <div className={styles[EUserRole.Coach].metaTextWrapper()}>
                    <Typography
                        text={`${participantState.firstName} ${participantState.lastName}`}
                        family={ETypographyFamily.DmSerif}
                        tag={ETypographyTag.Heading1}
                        weight={ETypographyWeight.Medium}
                        size={isMobile ? ETypographySize.Heading2 : ETypographySize.Heading1}
                        className={buildCssClassName(
                            styles[EUserRole.Coach].metaBigText(),
                            styles[EUserRole.Coach].loadingValue({
                                isBlurred: !isReady,
                                intensity: 5
                            })
                        )}
                    />
                    <Typography
                        text={participantState.cohortName}
                        family={ETypographyFamily.Nunito}
                        tag={ETypographyTag.Paragraph}
                        weight={ETypographyWeight.Default}
                        size={ETypographySize.Tiny}
                        className={buildCssClassName(
                            styles[EUserRole.Coach].metaSmallText(),
                            styles[EUserRole.Coach].loadingValue({
                                isBlurred: !isReady,
                                intensity: 3
                            })
                        )}
                    />
                </div>
            </div>
        )
    }

    function renderGroupMeta(): ReactElement {
        return (
            <div className={styles[EUserRole.Coach].metaWrapper()}>
                <div className={styles[EUserRole.Coach].metaTextWrapper()}>
                    <Typography
                        text={groupState.cohortName}
                        family={ETypographyFamily.DmSerif}
                        tag={ETypographyTag.Heading1}
                        weight={ETypographyWeight.Medium}
                        size={isMobile ? ETypographySize.Heading2 : ETypographySize.Heading1}
                        className={buildCssClassName(
                            styles[EUserRole.Coach].metaBigText(),
                            styles[EUserRole.Coach].loadingValue({
                                isBlurred: !isReady,
                                intensity: 5
                            })
                        )}
                    />
                    <Typography
                        text={groupState.groupName}
                        family={ETypographyFamily.Nunito}
                        tag={ETypographyTag.Paragraph}
                        weight={ETypographyWeight.Default}
                        size={ETypographySize.Tiny}
                        className={buildCssClassName(
                            styles[EUserRole.Coach].metaSmallText(),
                            styles[EUserRole.Coach].loadingValue({
                                isBlurred: !isReady,
                                intensity: 3
                            })
                        )}
                    />
                </div>
            </div>
        )
    }

    return (
        <HeaderMetaView role={EUserRole.Coach} motionKey="coach-meta" isVisible={isVisible}>
            {Boolean(isIndividualParticipant || isGroupParticipant) ? renderIndividualMeta() : null}

            {isGroup ? renderGroupMeta() : null}

            <CoachHeaderTabs tabs={tabs} />
        </HeaderMetaView>
    )
}

HeaderMetaCoach.displayName = "HeaderMetaCoach"

export { HeaderMetaCoach }
